<div class="agendamento-topo">
  <div>
    <h1 class="agendamento-titulo">Agendar serviço por posto de atendimento</h1>
    <p class="agendamento-subtitulo">
      @switch (passo().id) {
        @case ('servico') { Selecione o serviço que deseja agendar e confira as diretrizes antes de continuar. }
        @case ('posto') { Selecione o posto de atendimento desejado para dar andamento ao seu pedido de serviço. }
        @case ('data-hora') { Escolha a data e o horário para seu atendimento de forma rápida e segura. }
        @case ('confirmacao') { Revise as informações antes de confirmar o seu agendamento. }
        @default { Siga as orientações abaixo para um agendamento tranquilo e eficiente. }
      }
    </p>
  </div>
</div>

@if (passoAtual() === 0) {
  <section class="agendamento-orientacoes" aria-labelledby="agendamento-orientacoes-titulo">
    <h2 class="agendamento-orientacoes-titulo" id="agendamento-orientacoes-titulo">
      <i-lucide name="info" size="20" aria-hidden="true"></i-lucide>
      Orientações importantes
    </h2>

    <ol class="agendamento-orientacoes-lista">
      @for (orientacao of orientacoes; track $index) {
        <li class="agendamento-orientacao">
          <span class="agendamento-orientacao-numero" aria-hidden="true">{{ $index + 1 }}</span>
          {{ orientacao }}
        </li>
      }
    </ol>
  </section>
}

<ol class="agendamento-passos" aria-label="Etapas do agendamento">
  @for (item of passos; track item.id; let i = $index) {
    <li
      class="agendamento-passo"
      [attr.data-situacao]="situacaoDoPasso(i)"
      [attr.aria-current]="i === passoAtual() ? 'step' : null"
    >
      <span class="agendamento-passo-marca" aria-hidden="true">
        @if (situacaoDoPasso(i) === 'concluido') {
          <i-lucide name="check" size="18"></i-lucide>
        } @else {
          <i-lucide [name]="item.icone" size="18"></i-lucide>
        }
      </span>

      <span class="agendamento-passo-texto">
        <span class="agendamento-passo-titulo">{{ item.titulo }}</span>
        <span class="agendamento-passo-situacao">{{ rotuloSituacao(i) }}</span>
      </span>

      @if (i < passos.length - 1) {
        <span class="agendamento-passo-linha" aria-hidden="true"></span>
      }
    </li>
  }
</ol>

@switch (passo().id) {
  @case ('cidadao') {
    <section class="agendamento-cartao" aria-labelledby="agendamento-cidadao-titulo">
      <h2 class="agendamento-cartao-titulo" id="agendamento-cidadao-titulo">
        Selecione um cidadão para realizar o agendamento
      </h2>
      <p class="agendamento-cartao-texto">Escolha o cidadão que será atendido neste agendamento.</p>

      @if (estadoCidadaos() === 'sessao') {
        <div class="agendamento-aviso" role="status">
          <span class="agendamento-aviso-icone" aria-hidden="true">
            <i-lucide name="log-in" size="18"></i-lucide>
          </span>

          <div>
            <p class="agendamento-aviso-titulo">Sua sessão foi encerrada.</p>
            <p class="agendamento-aviso-texto">
              Entre novamente para agendar.
              <a class="agendamento-aviso-link" routerLink="/login">Entrar</a>
            </p>
          </div>
        </div>
      } @else {
        <label class="agendamento-rotulo" for="agendamento-cidadao">Cidadão</label>
        <div class="agendamento-select">
          <i-lucide class="agendamento-select-icone" name="user-plus" size="18" aria-hidden="true"></i-lucide>
          <!-- A opção marcada vai em cada `<option>`: um `[value]` no `<select>` é aplicado antes
               de o `@for` criar as opções, e ao voltar para esta etapa a escolha sumia. -->
          <select
            #campoCidadao
            id="agendamento-cidadao"
            class="agendamento-select-campo"
            [attr.aria-describedby]="estadoCidadaos() === 'carregando' ? 'agendamento-cidadao-carga' : null"
            (change)="aoSelecionarCidadao($event)"
          >
            <option value="" [selected]="cidadaoSelecionado() === ''">Selecione o cidadão</option>
            @for (cidadao of cidadaos(); track cidadao.id) {
              <option [value]="cidadao.id" [selected]="cidadao.id === cidadaoSelecionado()">
                {{ cidadao.nome }}
              </option>
            }
          </select>
        </div>

        @switch (estadoCidadaos()) {
          @case ('carregando') {
            <p class="agendamento-ajuda" id="agendamento-cidadao-carga" role="status" aria-live="polite">
              Carregando dependentes...
            </p>
          }

          @case ('falha') {
            <div class="agendamento-aviso agendamento-aviso-erro" role="alert">
              <span class="agendamento-aviso-icone" aria-hidden="true">
                <i-lucide name="circle-alert" size="18"></i-lucide>
              </span>

              <div class="agendamento-aviso-conteudo">
                <p class="agendamento-aviso-titulo">Não foi possível carregar seus dependentes.</p>
                <button type="button" class="agendamento-botao agendamento-aviso-botao" (click)="tentarNovamente()">
                  Tentar novamente
                </button>
              </div>
            </div>
          }
        }
      }
    </section>
  }

  @case ('servico') {
    <div class="agendamento-colunas">
      <section class="agendamento-cartao" aria-labelledby="agendamento-servico-titulo">
        <h2 class="agendamento-cartao-titulo" id="agendamento-servico-titulo">
          Selecione um serviço para realizar o agendamento
        </h2>
        <p class="agendamento-cartao-texto">
          Escolha o serviço que será realizado neste atendimento. A lista traz apenas os serviços que atendem por
          agendamento.
        </p>

        <label class="agendamento-rotulo" for="agendamento-servico">
          <i-lucide name="clipboard-list" size="15" aria-hidden="true"></i-lucide>
          Serviço
        </label>
        <div class="agendamento-select">
          <i-lucide class="agendamento-select-icone" name="file-text" size="18" aria-hidden="true"></i-lucide>
          <select
            #campoServico
            id="agendamento-servico"
            class="agendamento-select-campo"
            [attr.aria-describedby]="estadoServicos() === 'carregando' ? 'agendamento-servico-carga' : null"
            (change)="aoSelecionarServico($event)"
          >
            <option value="" [selected]="servicoSelecionado() === ''">Selecione o serviço</option>
            @for (servico of servicos(); track servico.id) {
              <option [value]="servico.id" [selected]="servico.id === servicoSelecionado()">
                {{ servico.nome }}
              </option>
            }
          </select>
        </div>

        @switch (estadoServicos()) {
          @case ('carregando') {
            <p class="agendamento-ajuda" id="agendamento-servico-carga" role="status" aria-live="polite">
              Carregando serviços...
            </p>
          }

          @case ('vazio') {
            <div class="agendamento-aviso" role="status">
              <span class="agendamento-aviso-icone" aria-hidden="true">
                <i-lucide name="calendar-x" size="18"></i-lucide>
              </span>

              <div>
                <p class="agendamento-aviso-titulo">Nenhum serviço disponível para agendamento no momento.</p>
                <p class="agendamento-aviso-texto">
                  Consulte os demais serviços em
                  <a class="agendamento-aviso-link" routerLink="/servicos">Todos os serviços</a>.
                </p>
              </div>
            </div>
          }

          @case ('falha') {
            <div class="agendamento-aviso agendamento-aviso-erro" role="alert">
              <span class="agendamento-aviso-icone" aria-hidden="true">
                <i-lucide name="circle-alert" size="18"></i-lucide>
              </span>

              <div class="agendamento-aviso-conteudo">
                <p class="agendamento-aviso-titulo">Não foi possível carregar os serviços.</p>
                <button
                  type="button"
                  class="agendamento-botao agendamento-aviso-botao"
                  (click)="tentarServicosNovamente()"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          }
        }
      </section>

      <aside class="agendamento-cartao agendamento-info" aria-labelledby="agendamento-diretrizes-titulo">
        <h2 class="agendamento-info-titulo" id="agendamento-diretrizes-titulo">
          <i-lucide name="list-checks" size="18" aria-hidden="true"></i-lucide>
          Diretrizes para a escolha do serviço
        </h2>

        <ul class="agendamento-diretrizes">
          @for (diretriz of diretrizesServico; track $index) {
            <li>
              <p class="agendamento-cartao-texto">{{ diretriz }}</p>
            </li>
          }
        </ul>
      </aside>
    </div>
  }

  @case ('posto') {
    <div class="agendamento-colunas">
      <section class="agendamento-cartao" aria-labelledby="agendamento-posto-titulo">
        <h2 class="agendamento-cartao-titulo" id="agendamento-posto-titulo">
          Selecione um posto para realizar o agendamento
        </h2>
        <p class="agendamento-cartao-texto">
          Escolha a unidade de atendimento mais conveniente para você. O horário de funcionamento é de
          responsabilidade de cada sub-posto municipal.
        </p>

        <label class="agendamento-rotulo" for="agendamento-posto">
          <i-lucide name="house" size="15" aria-hidden="true"></i-lucide>
          Posto de atendimento
        </label>
        <div class="agendamento-select">
          <i-lucide class="agendamento-select-icone" name="building-2" size="18" aria-hidden="true"></i-lucide>
          <select
            id="agendamento-posto"
            class="agendamento-select-campo"
            (change)="aoSelecionarPosto($event)"
          >
            <option value="" [selected]="postoSelecionado() === ''">Selecione o posto</option>
            @for (posto of postos(); track posto.id) {
              <option [value]="posto.id" [selected]="posto.id === postoSelecionado()">{{ posto.nome }}</option>
            }
          </select>
        </div>
      </section>

      <aside class="agendamento-cartao agendamento-info" aria-labelledby="agendamento-info-titulo">
        <h2 class="agendamento-info-titulo" id="agendamento-info-titulo">
          <i-lucide name="map-pin" size="18" aria-hidden="true"></i-lucide>
          Informações do posto
        </h2>

        <!-- O rótulo do select é só "logradouro, número – bairro"; o endereço completo (com o
             complemento, que distingue dois postos do mesmo prédio) aparece aqui. -->
        @if (postoEscolhido(); as posto) {
          <address class="agendamento-endereco">
            <span class="agendamento-endereco-rotulo">Endereço</span>
            {{ posto.endereco }}
          </address>
        }

        <p class="agendamento-cartao-texto">
          Após selecionar o posto, você verá a lista atualizada de datas e horários disponíveis na próxima etapa.
        </p>
        <p class="agendamento-cartao-texto">
          Certifique-se de comparecer com 15 minutos de antecedência no posto escolhido portando documento com foto
          original.
        </p>
      </aside>
    </div>
  }

  @case ('data-hora') {
    <!-- O horário escolhido lotou no envio (409): o aviso é anunciado ao voltar para cá. -->
    @if (avisoHorario()) {
      <div class="agendamento-aviso agendamento-aviso-erro agendamento-aviso-etapa" role="alert">
        <span class="agendamento-aviso-icone" aria-hidden="true">
          <i-lucide name="circle-alert" size="18"></i-lucide>
        </span>

        <p class="agendamento-aviso-titulo">{{ avisoHorario() }}</p>
      </div>
    }

    <section class="agendamento-cartao" aria-label="Data e horário">
      <div class="agendamento-data-hora">
        <div class="agendamento-calendario">
          <h2 class="agendamento-bloco-titulo">
            <i-lucide name="calendar" size="18" aria-hidden="true"></i-lucide>
            Selecione a data
          </h2>

          <div class="agendamento-calendario-navegacao">
            <button
              type="button"
              class="agendamento-calendario-seta"
              aria-label="Mês anterior"
              [disabled]="!podeVoltarMes()"
              (click)="mesAnterior()"
            >
              <i-lucide name="chevron-left" size="18" aria-hidden="true"></i-lucide>
            </button>

            <span class="agendamento-calendario-mes" aria-live="polite">{{ rotuloMes() }}</span>

            <button
              type="button"
              class="agendamento-calendario-seta"
              aria-label="Próximo mês"
              [disabled]="!podeAvancarMes()"
              (click)="proximoMes()"
            >
              <i-lucide name="chevron-right" size="18" aria-hidden="true"></i-lucide>
            </button>
          </div>

          <div class="agendamento-calendario-semana" aria-hidden="true">
            @for (dia of diasSemana; track $index) {
              <span>{{ dia }}</span>
            }
          </div>

          <!-- Os três estados do mês lido da agenda do posto, como no modal da página do serviço. -->
          @if (carregandoAgenda()) {
            <p class="agendamento-calendario-estado" role="status" aria-live="polite">
              <i-lucide name="loader-circle" size="16" aria-hidden="true"></i-lucide>
              Carregando os horários disponíveis...
            </p>
          } @else if (falhouAgenda()) {
            <div class="agendamento-calendario-estado agendamento-calendario-estado-falha" role="alert">
              <i-lucide name="circle-alert" size="16" aria-hidden="true"></i-lucide>
              <span>Não foi possível carregar a agenda.</span>
              <button type="button" class="agendamento-calendario-estado-botao" (click)="recarregarAgenda()">
                Tentar novamente
              </button>
            </div>
          } @else if (mesSemHorarios()) {
            <p class="agendamento-calendario-estado" role="status">
              <i-lucide name="calendar-x" size="16" aria-hidden="true"></i-lucide>
              Não há horários disponíveis neste mês.
            </p>
          }

          <div class="agendamento-calendario-grade">
            @for (dia of diasDoMes(); track dia.iso) {
              @if (dia.dia === null) {
                <span class="agendamento-dia-vazio"></span>
              } @else {
                <button
                  type="button"
                  class="agendamento-dia"
                  [class.agendamento-dia-hoje]="dia.hoje"
                  [class.agendamento-dia-selecionado]="dia.iso === dataSelecionada()"
                  [disabled]="dia.indisponivel"
                  [attr.aria-pressed]="dia.iso === dataSelecionada()"
                  (click)="selecionarDia(dia)"
                >
                  {{ dia.dia }}
                </button>
              }
            }
          </div>
        </div>

        <div class="agendamento-horarios">
          <h2 class="agendamento-bloco-titulo">
            <i-lucide name="clock" size="18" aria-hidden="true"></i-lucide>
            Selecione um Horário
          </h2>

          @switch (estadoHorarios()) {
            @case ('lista') {
              <div class="agendamento-horarios-grade">
                @for (horario of horariosDoDia(); track horario) {
                  <button
                    type="button"
                    class="agendamento-horario"
                    [class.agendamento-horario-selecionado]="horario === horarioSelecionado()"
                    [attr.aria-pressed]="horario === horarioSelecionado()"
                    (click)="selecionarHorario(horario)"
                  >
                    {{ horario }}
                  </button>
                }
              </div>
            }

            @case ('esgotado') {
              <!-- A data continua escolhida, mas o mês relido já não tem horário livre nela. -->
              <p class="agendamento-cartao-texto" role="status">
                Não há horários disponíveis nesta data. Escolha outra data.
              </p>
            }

            @case ('sem-data') {
              <p class="agendamento-cartao-texto">Escolha uma data para ver os horários disponíveis.</p>
            }
          }
        </div>
      </div>
    </section>

    @if (resumoDataHora()) {
      <p class="agendamento-resumo" role="status">
        <i-lucide name="info" size="18" aria-hidden="true"></i-lucide>
        Atendimento agendado para: {{ resumoDataHora() }}
      </p>
    }
  }

  @case ('confirmacao') {
    <section class="agendamento-cartao" aria-labelledby="agendamento-confirmacao-titulo">
      <div class="agendamento-confirmacao-topo">
        <div>
          <h2 class="agendamento-cartao-titulo" id="agendamento-confirmacao-titulo">
            Confirmação do atendimento
          </h2>
          <p class="agendamento-cartao-texto">Verifique os dados abaixo e confirme seu agendamento.</p>
        </div>

        <span class="agendamento-situacao">
          <span class="agendamento-situacao-ponto" aria-hidden="true"></span>
          Aguardando Confirmação
        </span>
      </div>

      <dl class="agendamento-resumo-lista">
        <div class="agendamento-resumo-item">
          <dt>Cidadão:</dt>
          <dd>{{ nomeCidadao() }}</dd>
        </div>
        <div class="agendamento-resumo-item">
          <dt>Serviço:</dt>
          <dd>{{ nomeServico() }}</dd>
        </div>
        <div class="agendamento-resumo-item">
          <dt>Posto:</dt>
          <dd>{{ nomePosto() }}</dd>
        </div>
        <div class="agendamento-resumo-item">
          <dt>Data:</dt>
          <dd>{{ dataFormatada() }}</dd>
        </div>
        <div class="agendamento-resumo-item">
          <dt>Horário:</dt>
          <dd>{{ horarioSelecionado() }}</dd>
        </div>
      </dl>
    </section>

    <!-- O erro do envio fica na própria etapa, sem o modal do interceptor: as escolhas ficam. -->
    @if (erroEnvio(); as erro) {
      <div class="agendamento-aviso agendamento-aviso-erro agendamento-aviso-etapa" role="alert">
        <span class="agendamento-aviso-icone" aria-hidden="true">
          <i-lucide name="circle-alert" size="18"></i-lucide>
        </span>

        <div class="agendamento-aviso-conteudo">
          <p class="agendamento-aviso-titulo">{{ erro.mensagem }}</p>

          @if (erro.detalhes.length) {
            <ul class="agendamento-aviso-detalhes">
              @for (detalhe of erro.detalhes; track $index) {
                <li>{{ detalhe }}</li>
              }
            </ul>
          }
        </div>
      </div>
    }

    <label class="agendamento-declaracao">
      <input
        type="checkbox"
        class="agendamento-declaracao-check"
        [checked]="declaracaoAceita()"
        (change)="alternarDeclaracao($event)"
      />
      Ao confirmar, você declara que as informações de agendamento estão corretas.
    </label>
  }
}

<div class="agendamento-acoes">
  @if (passoAtual() === passos.length - 1) {
    <span class="agendamento-vaga">
      <i-lucide name="badge-check" size="16" aria-hidden="true"></i-lucide>
      Vaga garantida mediante confirmação
    </span>
  }

  <button
    type="button"
    class="agendamento-botao agendamento-botao-voltar"
    [disabled]="passoAtual() === 0"
    (click)="voltar()"
  >
    <i-lucide name="arrow-left" size="16" aria-hidden="true"></i-lucide>
    Voltar
  </button>

  @if (passoAtual() === passos.length - 1) {
    <button
      type="button"
      class="agendamento-botao agendamento-botao-continuar"
      [disabled]="!podeConfirmar()"
      [attr.aria-busy]="enviando()"
      (click)="confirmar()"
    >
      <i-lucide name="check" size="16" aria-hidden="true"></i-lucide>
      Confirmar agendamento
    </button>
  } @else {
    <button
      type="button"
      class="agendamento-botao agendamento-botao-continuar"
      [disabled]="!podeAvancar()"
      (click)="avancar()"
    >
      Continuar
      <i-lucide name="arrow-right" size="16" aria-hidden="true"></i-lucide>
    </button>
  }
</div>

@if (passoAtual() !== passos.length - 1) {
<p class="agendamento-protecao">
  <span class="agendamento-protecao-icone" aria-hidden="true">
    <i-lucide name="shield-check" size="18"></i-lucide>
  </span>

  <span>
    <strong class="agendamento-protecao-titulo">Seus dados estão protegidos</strong>
    <span class="agendamento-protecao-texto">Utilizamos tecnologia segura para proteger suas informações.</span>
  </span>

  <a class="agendamento-protecao-link" href="https://www.gov.br/lgpd" target="_blank" rel="noopener">Saiba mais</a>
</p>
}
